import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Check, 
  X, 
  Loader2,
  User,
  UserPlus
} from 'lucide-react';
import { memberService, handleApiError } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const MembersManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [editMemberName, setEditMemberName] = useState('');
  const [editMemberEmail, setEditMemberEmail] = useState('');
  const [deleting, setDeleting] = useState(null);

  // Load members
  const loadMembers = async () => {
    try {
      setLoading(true);
      const membersData = await memberService.getAllMembers();
      setMembers(membersData);
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible de charger les membres: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, []);

  // Add new member
  const handleAddMember = async () => {
    if (!newMemberName.trim()) {
      toast({
        title: "Erreur",
        description: "Le nom du membre est requis",
        variant: "destructive"
      });
      return;
    }

    try {
      await memberService.create({ 
        name: newMemberName.trim(),
        email: newMemberEmail.trim(),
        active: true 
      });
      
      toast({
        title: "Succès",
        description: "Membre ajouté avec succès",
      });
      
      setNewMemberName('');
      setNewMemberEmail('');
      setIsAdding(false);
      loadMembers();
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible d'ajouter le membre: ${errorMessage}`,
        variant: "destructive"
      });
    }
  };

  // Start editing member
  const startEditing = (member) => {
    setEditingMember(member.id);
    setEditMemberName(member.name);
    setEditMemberEmail(member.email || '');
  };

  // Save member edit
  const handleSaveMember = async (memberId) => {
    if (!editMemberName.trim()) {
      toast({
        title: "Erreur",
        description: "Le nom du membre est requis",
        variant: "destructive"
      });
      return;
    }

    try {
      await memberService.update(memberId, { 
        name: editMemberName.trim(),
        email: editMemberEmail.trim(),
        active: true 
      });
      
      toast({
        title: "Succès",
        description: "Membre modifié avec succès",
      });
      
      setEditingMember(null);
      setEditMemberName('');
      setEditMemberEmail('');
      loadMembers();
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible de modifier le membre: ${errorMessage}`,
        variant: "destructive"
      });
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingMember(null);
    setEditMemberName('');
    setEditMemberEmail('');
  };

  // Delete member
  const handleDeleteMember = async (memberId, memberName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le membre "${memberName}" ?`)) {
      return;
    }

    try {
      setDeleting(memberId);
      await memberService.delete(memberId);
      
      toast({
        title: "Succès",
        description: "Membre supprimé avec succès",
      });
      
      loadMembers();
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible de supprimer le membre: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setDeleting(null);
    }
  };

  // Toggle member active status
  const toggleMemberStatus = async (member) => {
    try {
      await memberService.update(member.id, { 
        name: member.name,
        email: member.email || '',
        active: !member.active 
      });
      
      toast({
        title: "Succès",
        description: `Membre ${member.active ? 'désactivé' : 'activé'} avec succès`,
      });
      
      loadMembers();
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible de modifier le statut: ${errorMessage}`,
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-green-600" />
            <p className="text-gray-600">Chargement des membres...</p>
          </div>
        </div>
      </div>
    );
  }

  const activeMembers = members.filter(m => m.active);
  const inactiveMembers = members.filter(m => !m.active);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')}
              className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Gestion des Membres
              </h1>
              <p className="text-gray-600">{user?.name}</p>
            </div>
          </div>
        </div>
        
        <Button 
          onClick={() => setIsAdding(true)}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Nouveau membre
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card className="border-indigo-200 bg-indigo-50 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center">
              <User className="w-10 h-10 text-indigo-600 mr-4" />
              <div>
                <p className="text-2xl font-bold text-indigo-700">{activeMembers.length}</p>
                <p className="text-indigo-600">Membres actifs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-gray-200 bg-gray-50 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center">
              <Users className="w-10 h-10 text-gray-600 mr-4" />
              <div>
                <p className="text-2xl font-bold text-gray-700">{members.length}</p>
                <p className="text-gray-600">Total membres</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add new member form */}
      {isAdding && (
        <Card className="border-blue-200 bg-blue-50 shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-blue-700 flex items-center">
              <Plus className="w-5 h-5 mr-2" />
              Ajouter un nouveau membre
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="newMemberName">Nom du membre</Label>
                  <Input
                    id="newMemberName"
                    value={newMemberName}
                    onChange={(e) => setNewMemberName(e.target.value)}
                    placeholder="Entrez le nom du membre..."
                    className="border-blue-200 focus:border-blue-400"
                  />
                </div>
                <div>
                  <Label htmlFor="newMemberEmail">Email (optionnel)</Label>
                  <Input
                    id="newMemberEmail"
                    type="email"
                    value={newMemberEmail}
                    onChange={(e) => setNewMemberEmail(e.target.value)}
                    placeholder="exemple@email.com"
                    className="border-blue-200 focus:border-blue-400"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={handleAddMember}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setIsAdding(false);
                    setNewMemberName('');
                    setNewMemberEmail('');
                  }}
                  className="border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Annuler
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Members */}
      <Card className="border-green-200 bg-white shadow-md mb-6">
        <CardHeader>
          <CardTitle className="text-green-700 flex items-center">
            <User className="w-5 h-5 mr-2" />
            Membres Actifs ({activeMembers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeMembers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aucun membre actif</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeMembers.map((member) => (
                <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-green-600" />
                    </div>
                    
                    {editingMember === member.id ? (
                      <div className="space-y-2">
                        <Input
                          value={editMemberName}
                          onChange={(e) => setEditMemberName(e.target.value)}
                          className="border-blue-200 focus:border-blue-400 w-64"
                          placeholder="Nom du membre"
                        />
                        <Input
                          type="email"
                          value={editMemberEmail}
                          onChange={(e) => setEditMemberEmail(e.target.value)}
                          className="border-blue-200 focus:border-blue-400 w-64"
                          placeholder="Email (optionnel)"
                        />
                      </div>
                    ) : (
                      <div>
                        <p className="font-medium text-gray-800">{member.name}</p>
                        {member.email && (
                          <p className="text-sm text-blue-600">{member.email}</p>
                        )}
                        <p className="text-sm text-gray-500">
                          Membre depuis {new Date(member.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    )}
                    
                    <Badge className="bg-green-100 text-green-700">Actif</Badge>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {editingMember === member.id ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleSaveMember(member.id)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={cancelEditing}
                          className="border-gray-200 text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEditing(member)}
                          className="border-blue-200 text-blue-600 hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleMemberStatus(member)}
                          className="border-orange-200 text-orange-600 hover:bg-orange-50"
                        >
                          Désactiver
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteMember(member.id, member.name)}
                          disabled={deleting === member.id}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                        >
                          {deleting === member.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inactive Members */}
      {inactiveMembers.length > 0 && (
        <Card className="border-gray-200 bg-white shadow-md">
          <CardHeader>
            <CardTitle className="text-gray-700 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Membres Inactifs ({inactiveMembers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {inactiveMembers.map((member) => (
                <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-600">{member.name}</p>
                      {member.email && (
                        <p className="text-sm text-blue-600">{member.email}</p>
                      )}
                      <p className="text-sm text-gray-400">Inactif</p>
                    </div>
                    <Badge variant="secondary" className="bg-gray-200 text-gray-600">Inactif</Badge>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleMemberStatus(member)}
                      className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                    >
                      Réactiver
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteMember(member.id, member.name)}
                      disabled={deleting === member.id}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                    >
                      {deleting === member.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
    </div>
  );
};

export default MembersManagement;